import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useWallet } from '@solana/wallet-adapter-react';
import { ClientMatchState } from '../../state/roomStore';
import { soundManager } from '../../utils/sounds';
import { triggerScreenShake } from './arcade';

interface Props {
  state?: ClientMatchState;
  isMyTurn: boolean;
  selectedPowerCardId?: string | null;
  selectedCardIds: string[];
  onSelectionChange: (cardIds: string[]) => void;
  onPlay: (cardIds: string[], powerCardIds: string[]) => Promise<void> | void;
  timeRemainingSeconds?: number | null;
  walletOverride?: string;
}

const MAX_PLAYABLE_CARDS = 5;
const TEMPO_CAP = 3;

export const HandSelector: React.FC<Props> = ({
  state,
  isMyTurn,
  selectedPowerCardId,
  selectedCardIds,
  onSelectionChange,
  onPlay,
  timeRemainingSeconds,
  walletOverride,
}) => {
  const { publicKey } = useWallet();
  const wallet = walletOverride ?? publicKey?.toBase58();
  const [submitting, setSubmitting] = useState(false);

  const isMine = useMemo(
    () => state?.currentHandOwner === wallet,
    [state?.currentHandOwner, wallet],
  );

  const handCards = useMemo(() => {
    if (!isMine) return [];
    return state?.currentHand ?? [];
  }, [state?.currentHand, isMine]);

  useEffect(() => {
    setSubmitting(false);
  }, [state?.currentHandSequence]);

  const deckState = useMemo(() => {
    if (!wallet) return undefined;
    return state?.deckStates?.[wallet];
  }, [state?.deckStates, wallet]);

  const timeRemaining =
    typeof timeRemainingSeconds === 'number' && timeRemainingSeconds >= 0
      ? timeRemainingSeconds
      : null;

  const myTempo = useMemo(() => {
    if (!wallet) return 0;
    return state?.tempoState?.[wallet] ?? 0;
  }, [state?.tempoState, wallet]);

  const tempoBadgeClass = useMemo(() => {
    if (myTempo >= TEMPO_CAP) {
      return 'bg-arcade-cyan/25 neon-text border border-arcade-cyan/60 animate-pulseGlow';
    }
    if (myTempo > 0) {
      return 'bg-emerald-500/15 text-emerald-200 border border-emerald-400/20';
    }
    return 'bg-white/10 text-white/50 border border-white/10';
  }, [myTempo]);

  const handlePlay = useCallback(async () => {
    if (!isMyTurn || !isMine || selectedCardIds.length === 0) return;
    try {
      setSubmitting(true);
      soundManager.play('powerup');
      triggerScreenShake('medium');
      const powerIds = selectedPowerCardId ? [selectedPowerCardId] : [];
      await onPlay(selectedCardIds, powerIds);
    } catch (error) {
      soundManager.play('error');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  }, [isMyTurn, isMine, selectedCardIds, selectedPowerCardId, onPlay]);

  const handleClearSelection = useCallback(() => {
    if (selectedCardIds.length > 0) {
      soundManager.play('click');
      onSelectionChange([]);
    }
  }, [selectedCardIds.length, onSelectionChange]);

  // Keyboard shortcuts: 1-6 to toggle cards, Space to play, Escape to clear
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        !isMyTurn ||
        !isMine
      ) {
        return;
      }

      // Number keys 1-6 toggle card selection
      if (e.key >= '1' && e.key <= '6') {
        e.preventDefault();
        const index = parseInt(e.key, 10) - 1;
        if (index < handCards.length) {
          const cardId = handCards[index].id;
          const isSelected = selectedCardIds.includes(cardId);

          if (isSelected) {
            // Deselect card
            soundManager.play('click');
            onSelectionChange(selectedCardIds.filter((id) => id !== cardId));
          } else if (selectedCardIds.length < MAX_PLAYABLE_CARDS) {
            // Select card
            soundManager.play('click');
            onSelectionChange([...selectedCardIds, cardId]);
          } else {
            // Max cards selected
            soundManager.play('error');
          }
        }
      }

      // Space to play hand
      if (e.key === ' ' && selectedCardIds.length > 0 && !submitting) {
        e.preventDefault();
        handlePlay();
      }

      // Escape to clear selection
      if (e.key === 'Escape' && selectedCardIds.length > 0) {
        e.preventDefault();
        handleClearSelection();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isMyTurn,
    isMine,
    handCards,
    selectedCardIds,
    submitting,
    onSelectionChange,
    handlePlay,
    handleClearSelection,
  ]);

  // Minimal control bar - more compact on mobile
  return (
    <section className="arcade-panel pixel-corners flex items-center justify-between rounded-xl px-2 md:px-4 py-2 md:py-3 text-sm text-white/80 h-full">
      {/* Left: Status info - hidden on mobile to save space */}
      <div className="hidden md:flex items-center gap-3">
        <span className="font-arcade text-[10px] uppercase tracking-wide text-arcade-cyan">
          HAND
        </span>
        <span
          className={`rounded px-1.5 py-0.5 font-pixel text-[9px] font-semibold uppercase ${tempoBadgeClass}`}
        >
          ⚡{Math.min(myTempo, TEMPO_CAP)}
        </span>
        <span
          className={`rounded px-1.5 py-0.5 font-pixel text-[9px] font-semibold uppercase ${
            timeRemaining === null
              ? 'bg-white/10 text-white/50'
              : timeRemaining <= 5
                ? 'bg-arcade-pink/30 text-arcade-pink animate-pulse'
                : timeRemaining <= 15
                  ? 'bg-amber-400/20 text-amber-200'
                  : 'bg-white/10 text-white/70'
          }`}
        >
          {timeRemaining === null ? '—' : `${timeRemaining}s`}
        </span>
      </div>

      {/* Center: Selection + Deck info */}
      <div className="flex items-center gap-2 md:gap-4">
        {!wallet ? (
          <span className="text-xs text-white/50">🔒 Connect</span>
        ) : !isMine ? (
          <motion.span
            className="text-xs text-white/50"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            ⏳ Waiting...
          </motion.span>
        ) : handCards.length === 0 ? (
          <motion.span
            className="text-xs text-arcade-cyan"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ repeat: Infinity, duration: 1 }}
          >
            ◈ Dealing...
          </motion.span>
        ) : (
          <>
            <div className="flex items-center gap-1 md:gap-2">
              <span
                className={`font-bold text-sm ${selectedCardIds.length > 0 ? 'text-arcade-cyan' : 'text-white/40'}`}
              >
                {selectedCardIds.length}/{MAX_PLAYABLE_CARDS}
              </span>
              {selectedCardIds.length > 0 && (
                <button
                  type="button"
                  onClick={handleClearSelection}
                  className="text-[10px] md:text-xs text-white/40 hover:text-white/70 transition"
                >
                  ✕
                </button>
              )}
            </div>
            <span className="hidden md:inline text-white/20">|</span>
            <div className="hidden md:flex items-center gap-2 text-xs text-white/50">
              <span>Deck: {deckState?.drawPile.length ?? 0}</span>
              <span>Void: {deckState?.discardPile.length ?? 0}</span>
            </div>
          </>
        )}
      </div>

      {/* Right: Play button + keyboard hints */}
      <div className="flex items-center gap-2">
        {/* Keyboard hint - only show when it's player's turn */}
        {isMyTurn && isMine && handCards.length > 0 && (
          <span
            className="hidden md:inline text-[9px] text-white/30"
            title="Keyboard shortcuts: 1-6 select cards, Space to play, Esc to clear"
          >
            [1-6] [Space]
          </span>
        )}
        {selectedPowerCardId && (
          <span className="hidden md:inline rounded bg-arcade-purple/20 px-2 py-0.5 text-[9px] uppercase text-arcade-purple">
            ⚔ Power
          </span>
        )}
        <motion.button
          type="button"
          onClick={handlePlay}
          disabled={!isMyTurn || !isMine || selectedCardIds.length === 0 || submitting}
          className="arcade-btn pixel-corners rounded-lg px-3 md:px-4 py-1.5 md:py-2 font-arcade text-xs md:text-sm uppercase tracking-wide text-white shadow-neon transition disabled:cursor-not-allowed disabled:opacity-40"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title="Press Space to play"
        >
          {submitting ? '...' : '▸ PLAY'}
        </motion.button>
      </div>
    </section>
  );
};
